import { SWRConfig } from "swr";

import Bugsnag from "@bugsnag/js";
import UAParser from "ua-parser-js";

import { insightDetailUrl } from "components/InsightLongForm/InsightDetail";
import { profileUrl } from "components/InsightLongForm/ProfileCard";
import Page, { getInsightUrl } from "components/InsightPage";
import {
  downstreamInsightsUrl,
  originatedInsightsUrl,
  upstreamInsightsUrl,
} from "components/InsightStreamCard";
import { NextPageWithLayout } from "pages/_app";
import Error from "pages/_error";
import { baseFetcher } from "utils/baseFetcher";

const InsightsDetailPage: NextPageWithLayout = ({
  post,
  entity,
  contentVertical,
  trendingInsights,
  latestInsights,
  account,
}: any) => {
  return (
    <Page
      post={post?.data}
      entity={entity}
      contentVertical={contentVertical}
      trendingInsights={trendingInsights}
      latestInsights={latestInsights}
      account={account}
    />
  );
};

export async function getServerSideProps(context: any) {
  try {
    const slug = context.params.slug;
    const lang = context.query?.lang;

    const userAgent: string = context.req.headers["user-agent"] || "";

    const deviceType = new UAParser(userAgent).getDevice().type;
    const isMobile = deviceType === "mobile";

    const fetchInsight = async () => {
      const [insight, statusCode] = await baseFetcher(
        getInsightUrl(slug, lang),
        context,
        {
          includeStatusCode: true,
          fetchOptions: { signal: AbortSignal.timeout(5000) },
        }
      );
      let author, insightDetail;
      if (insight?.author) {
        author = await baseFetcher(profileUrl(insight.author), context, {
          fetchOptions: { signal: AbortSignal.timeout(2000) },
        });
      }
      if (insight?.fromSearchBot) {
        insightDetail = await baseFetcher(insightDetailUrl(slug), context, {
          fetchOptions: { signal: AbortSignal.timeout(2000) },
        });
      }
      return [insight, author, insightDetail, statusCode];
    };

    const [
      [insight, author, insightDetail, statusCode],
      downstreamInsights,
      upstreamInsights,
      originatedInsights,
    ] = await Promise.all([
      fetchInsight(),
      isMobile
        ? Promise.resolve(null)
        : baseFetcher(downstreamInsightsUrl(slug), context, {
            fetchOptions: { signal: AbortSignal.timeout(2000) },
          }),
      isMobile
        ? Promise.resolve(null)
        : baseFetcher(upstreamInsightsUrl(slug), context, {
            fetchOptions: { signal: AbortSignal.timeout(2000) },
          }),
      isMobile
        ? Promise.resolve(null)
        : baseFetcher(originatedInsightsUrl(slug), context, {
            fetchOptions: { signal: AbortSignal.timeout(2000) },
          }),
    ]);

    if (statusCode === 400 || statusCode > 404) {
      return { props: { errorCode: statusCode } };
    }

    if (!insight) return { notFound: true };

    return {
      props: {
        fallback: {
          [getInsightUrl(slug, lang)]: insight,
          ...(author ? { [profileUrl(author)]: author } : {}),
          ...(insight.fromSearchBot
            ? { [insightDetailUrl(slug)]: insightDetail }
            : {}),
          [downstreamInsightsUrl(slug)]: downstreamInsights,
          [upstreamInsightsUrl(slug)]: upstreamInsights,
          [originatedInsightsUrl(slug)]: originatedInsights,
        },
      },
    };
  } catch (error: any) {
    console.error("[slug].tsx ~ getServerSideProps ~ error", error);
    Bugsnag.notify(error);

    return { notFound: true };
  }
}

InsightsDetailPage.getLayout = (page, { pageProps }) => {
  const { fallback, errorCode } = pageProps;

  if (errorCode) return <Error statusCode={errorCode} />;

  return <SWRConfig value={{ fallback }}>{page}</SWRConfig>;
};

export default InsightsDetailPage;
